import { AuthTokens, LoginCredentials, SessionConflictResponse, ConfirmReplaceRequest } from '../types/auth.types';
import {
  LoginResponse,
  LoginResponseAccess,
  MeV2Response,
} from '../types/multitenancy.types';
import { errorHandler } from '../utils/errorHandler';
import { showToast } from '../utils/toast';
import webSocketManager from '../utils/websocket-manager';
import sessionService from './session.service';
import tabSyncManager from './tab-sync-manager';
import { emitAuthTokenRefreshed } from '../utils/authTokenEmitter';
import {
  SANDBOX_HEADER_NAME,
  classifySandboxRejection,
  clearSession as clearSandboxSession,
  getSandboxHeaderForRequest,
  isPotentialSandboxAttack,
} from '../utils/sandboxSession';

const API_URL = '/api/v1';

/**
 * Специальная ошибка для конфликта сессий (HTTP 409)
 */
export class SessionConflictError extends Error {
  constructor(public conflictData: SessionConflictResponse) {
    super('Session conflict detected');
    this.name = 'SessionConflictError';
  }
}

/**
 * Специальная ошибка для случаев, когда редирект на login уже выполнен
 */
class TokenRefreshRedirectError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TokenRefreshRedirectError';
  }
}

class AuthService {
  private tokenKey = 'auth_tokens';
  private currentToken: string | null = null;
  private refreshPromise: Promise<AuthTokens> | null = null;
  private tokenExpiryTimer: NodeJS.Timeout | null = null;
  
  async login(credentials: LoginCredentials): Promise<AuthTokens> {
    try {
      const payload = JSON.stringify(credentials);
      console.log('[AuthService] Login attempt with payload:', payload);
      console.log('[AuthService] Device fingerprint:', credentials.device_fingerprint);

      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: payload,
      });

      console.log('[AuthService] Login response status:', response.status);
      console.log('[AuthService] Response headers:', Object.fromEntries(response.headers.entries()));

      // Обработка конфликта сессий (HTTP 409)
      if (response.status === 409) {
        const conflictData: SessionConflictResponse = await response.json();
        console.log('[AuthService] Session conflict detected:', conflictData);
        throw new SessionConflictError(conflictData);
      }

      if (!response.ok) {
        const error = await errorHandler.handleFetchError(response);
        throw new Error(error.message);
      }

      const tokens: AuthTokens = await response.json();
      console.log('[AuthService] Login successful, received tokens');
      console.log('[AuthService] Access token (first 20 chars):', tokens.access_token?.substring(0, 20) + '...');

      this.saveTokens(tokens);
      this.currentToken = tokens.access_token;
      this.scheduleTokenRefresh(tokens.expires_in);
      return tokens;
    } catch (error) {
      console.error('[AuthService] Login error:', error);
      throw error;
    }
  }

  /**
   * Подтверждение замены активных сессий на других устройствах
   * Вызывается после получения временного токена из ответа 409
   */
  async confirmReplace(payload: ConfirmReplaceRequest): Promise<AuthTokens> {
    try {
      const payloadStr = JSON.stringify(payload);
      console.log('[AuthService] Confirm replace with payload:', payloadStr);

      const response = await fetch(`${API_URL}/auth/login/confirm-replace`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: payloadStr,
      });

      console.log('[AuthService] Confirm replace response status:', response.status);
      console.log('[AuthService] Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const error = await errorHandler.handleFetchError(response);
        throw new Error(error.message);
      }

      const tokens: AuthTokens = await response.json();
      console.log('[AuthService] Confirm replace successful, received tokens');
      console.log('[AuthService] Access token (first 20 chars):', tokens.access_token?.substring(0, 20) + '...');

      this.saveTokens(tokens);
      this.currentToken = tokens.access_token;
      this.scheduleTokenRefresh(tokens.expires_in);
      return tokens;
    } catch (error) {
      console.error('[AuthService] Confirm replace error:', error);
      throw error;
    }
  }
  
  /**
   * Multi-tenancy v2 login. Возвращает либо access_token (single-org user),
   * либо pre_auth_token + список организаций для выбора (multi-org user).
   * Токены в localStorage сохраняем ТОЛЬКО для bearer-ветки; pre_auth — это
   * короткоживущий (5 мин) токен только для /auth/select-organization,
   * хранить его в localStorage не нужно (caller держит in-memory).
   */
  async loginV2(credentials: LoginCredentials): Promise<LoginResponse> {
    // login-v2 ожидает поле `username`, а внутри фронта мы исторически зовём его `login`.
    const payload = JSON.stringify({
      username: credentials.login,
      password: credentials.password,
      ...(credentials.device_fingerprint ? { device_fingerprint: credentials.device_fingerprint } : {}),
    });

    const response = await fetch(`${API_URL}/auth/login-v2`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
    });

    if (response.status === 409) {
      const conflictData: SessionConflictResponse = await response.json();
      throw new SessionConflictError(conflictData);
    }

    if (!response.ok) {
      const error = await errorHandler.handleFetchError(response);
      throw new Error(error.message);
    }

    const data: LoginResponse = await response.json();

    if (data.token_type === 'bearer') {
      this.persistAccessToken(data);
    }

    return data;
  }

  /**
   * Multi-tenancy confirm-replace: подтвердить замену сессий после 409 от /auth/login-v2.
   * Возвращает discriminated union (bearer | pre_auth) как loginV2.
   * Backend выставляет HttpOnly cookies (access + refresh) для bearer-ветки.
   * Для pre_auth-ветки cookies НЕ ставятся — они придут после /auth/select-organization.
   */
  async confirmReplaceV2(payload: ConfirmReplaceRequest): Promise<LoginResponse> {
    const response = await fetch(`${API_URL}/auth/login-v2/confirm-replace`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, replace_sessions: true }),
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await errorHandler.handleFetchError(response);
      throw new Error(error.message);
    }

    const data: LoginResponse = await response.json();

    if (data.token_type === 'bearer') {
      this.persistAccessToken(data);
    }

    return data;
  }

  /**
   * Обмен pre_auth_token + organization_id → access_token.
   * Auth header передаётся явно — pre_auth не лежит в localStorage и недоступен через getAuthHeaders().
   */
  async selectOrganization(preAuthToken: string, organizationId: number): Promise<LoginResponseAccess> {
    const response = await fetch(`${API_URL}/auth/select-organization`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${preAuthToken}`,
      },
      body: JSON.stringify({ organization_id: organizationId }),
    });

    if (!response.ok) {
      const error = await errorHandler.handleFetchError(response);
      throw new Error(error.message);
    }

    const data: LoginResponseAccess = await response.json();
    this.persistAccessToken(data);
    return data;
  }

  /**
   * Переключение active organization без перелогина.
   * Возвращает новый access_token с другим organization_id claim'ом.
   */
  async switchOrganization(organizationId: number): Promise<LoginResponseAccess> {
    const response = await this.fetchWithAuth(`${API_URL}/auth/switch-organization`, {
      method: 'POST',
      body: JSON.stringify({ organization_id: organizationId }),
    });

    if (!response.ok) {
      const error = await errorHandler.handleFetchError(response);
      throw new Error(error.message);
    }

    const data: LoginResponseAccess = await response.json();
    this.persistAccessToken(data);
    return data;
  }

  /**
   * Текущий user + активная организация + список доступных.
   */
  async meV2(): Promise<MeV2Response> {
    const response = await this.fetchWithAuth(`${API_URL}/auth/me-v2`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await errorHandler.handleFetchError(response);
      throw new Error(error.message);
    }

    return response.json();
  }

  /**
   * Сохранить access_token из v2-ответа (где refresh_token приходит через cookie,
   * а expires_in отсутствует) в формате AuthTokens. exp вытаскивается из JWT.
   */
  private persistAccessToken(resp: LoginResponseAccess): void {
    const prev = this.getTokens();
    const expiresIn = this.extractExpiresIn(resp.access_token);
    const tokens: AuthTokens = {
      access_token: resp.access_token,
      refresh_token: prev?.refresh_token || '',
      token_type: resp.token_type || 'bearer',
      expires_in: expiresIn,
    };

    this.saveTokens(tokens);
    this.currentToken = tokens.access_token;
    this.scheduleTokenRefresh(tokens.expires_in > 0 ? tokens.expires_in : 15 * 60);
    emitAuthTokenRefreshed();
  }

  private decodeJwtPayload(token: string): Record<string, unknown> | null {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch {
      return null;
    }
  }

  /**
   * Время истечения pre_auth токена (ms, Unix epoch) из JWT `exp`.
   * Используется на странице выбора организации для обратного отсчёта.
   * Возвращает null, если токен не JWT или без `exp`.
   */
  getPreAuthExpiry(token: string): number | null {
    const payload = this.decodeJwtPayload(token);
    return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
  }

  private extractExpiresIn(accessToken: string): number {
    const payload = this.decodeJwtPayload(accessToken);
    if (typeof payload?.exp === 'number') {
      const seconds = payload.exp - Math.floor(Date.now() / 1000);
      return seconds > 0 ? seconds : 0;
    }
    return 0;
  }

  async logout(): Promise<void> {
    const tokens = this.getTokens();

    // Сначала закрываем все WebSocket соединения
    try {
      webSocketManager.closeAll();
    } catch (error) {
      console.error('Error closing WebSocket connections during logout:', error);
    }

    if (tokens?.access_token) {
      try {
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${tokens.access_token}`,
            'Content-Type': 'text/plain',
          },
          body: tokens.refresh_token || null,
        });
      } catch (error) {
        console.error('Logout API error:', error);
      }
    }

    tabSyncManager.broadcastLogout();
    this.clearTokenRefreshTimer();
    this.removeTokens();
    this.clearTelephonyData();
    this.currentToken = null;
    this.refreshPromise = null;
  }
  
  getTokens(): AuthTokens | null {
    const tokensString = localStorage.getItem(this.tokenKey);
    if (!tokensString) return null;
    
    try {
      return JSON.parse(tokensString);
    } catch {
      return null;
    }
  }
  
  private saveTokens(tokens: AuthTokens): void {
    try {
      localStorage.setItem(this.tokenKey, JSON.stringify(tokens));
      tabSyncManager.notifyTokenUpdate(tokens.access_token, tokens.expires_in);
    } catch (error) {
      console.error('[AuthService] Не удалось сохранить токены:', error);
      showToast('Ошибка сохранения сессии', 'error');
    }
  }

  private removeTokens(): void {
    try {
      localStorage.removeItem(this.tokenKey);
    } catch (error) {
      console.error('[AuthService] Не удалось удалить токены:', error);
    }
  }

  /**
   * Очистить все данные телефонии из localStorage
   * Удаляет ключи с префиксами crmphone и CRM_PHONE, установленные виджетом
   */
  private clearTelephonyData(): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('crmphone') || key.startsWith('CRM_PHONE'))) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
  }
  
  setupAxiosInterceptors(token: string): void {
    this.currentToken = token;
  }

  /**
   * Редирект на страницу логина с указанием причины
   */
  private async redirectToLoginWithReason(reason: string): Promise<void> {
    // Закрываем все WebSocket соединения перед выходом
    try {
      webSocketManager.closeAll();
    } catch (wsError) {
      console.error('[auth.service] Error closing WebSocket connections:', wsError);
    }

    try {
      await this.logout();
    } catch (logoutError) {
      console.warn('[auth.service] Logout failed during forced invalidation', logoutError);
    }
    window.location.href = `/login?reason=${reason}`;
  }

  /**
   * Проверить причину 401 ошибки по detail в ответе
   */
  private async check401Reason(response: Response): Promise<'session_invalidated' | 'blocked' | 'token_expired' | null> {
    try {
      const clone = response.clone();
      const data = await clone.json();
      const detail = data?.detail;

      if (typeof detail === 'string') {
        if (detail.includes('завершена администратором') || detail.includes('session was terminated')) {
          return 'session_invalidated';
        }
        if (detail.includes('заблокирован') || detail.includes('blocked')) {
          return 'blocked';
        }
      }

      return 'token_expired';
    } catch (error) {
      // Если не удалось распарсить JSON, считаем что токен просто истёк
      return 'token_expired';
    }
  }

  getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Если токен не инициализирован (например, после перезагрузки), попробуем взять его из localStorage
    if (!this.currentToken) {
      const tokens = this.getTokens();
      if (tokens?.access_token) {
        this.currentToken = tokens.access_token;
      }
    }
    if (this.currentToken) {
      headers['Authorization'] = `Bearer ${this.currentToken}`;
    }

    return headers;
  }
  
  async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    // Проверяем, не истек ли токен
    await this.ensureValidToken();

    const mergedHeaders = new Headers(this.getAuthHeaders());

    // AB3 Academy: X-Sandbox-Session header — добавляем когда активна practice-сессия.
    // Backend middleware (Phase 2+) маршрутизирует запрос в sandbox PG-schema.
    // /api/v1/auth/* исключается внутри getSandboxHeaderForRequest().
    const sandboxHeader = getSandboxHeaderForRequest(url);
    if (sandboxHeader && !mergedHeaders.has(SANDBOX_HEADER_NAME)) {
      mergedHeaders.set(SANDBOX_HEADER_NAME, sandboxHeader);
    }

    if (options.headers) {
      const customHeaders = new Headers(options.headers as HeadersInit);
      customHeaders.forEach((value, key) => {
        mergedHeaders.set(key, value);
      });
    }

    const body = options.body;
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
    const isBlob = typeof Blob !== 'undefined' && body instanceof Blob;
    const isArrayBuffer = typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer;
    const isURLSearchParams = typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams;

    if (isFormData || isBlob || isArrayBuffer || isURLSearchParams) {
      mergedHeaders.delete('Content-Type');
    }

    let response = await fetch(url, {
      ...options,
      headers: mergedHeaders,
    });

    // AB3 Academy sandbox interceptors (FINAL handoff §6):
    //   410 + "sandbox"          → TTL истёк / watchdog дропнул схему.
    //   403 + "signature" or
    //   "session does not belong" → HMAC mismatch или cross-user attack (Sentry).
    //   400 + "sandbox"          → битый base64 / extension-сабмит.
    // Все три ведут к одному действию: clear store, dispatch event.
    // UI слушает ab3-sandbox-{expired,forged,invalid} CustomEvent.
    //
    // Перф-гард (simplify PR #226): JSON peek дорогой, делаем его только если
    // sandbox активен для этого URL — иначе ни один из 3-х кодов не может быть
    // sandbox-related. У учащихся без active practice и у всех пользователей
    // без Academy этот блок будет no-op.
    if (
      (response.status === 410 || response.status === 403 || response.status === 400) &&
      getSandboxHeaderForRequest(url) !== null
    ) {
      try {
        const peek = await response.clone().json();
        const detail = (peek && (peek as { detail?: unknown }).detail) ?? null;
        const kind = classifySandboxRejection(response.status, detail);
        if (kind) {
          clearSandboxSession(kind);
          if (isPotentialSandboxAttack(kind)) {
            // 403 HMAC mismatch — потенциальный attack или забытый switch-org.
            // На уровне errorHandler можно подвесить Sentry-листенер.
            // Здесь логируем минимально (структурированно), без личных данных.
            console.warn('[sandbox] header rejected', { status: response.status, detail });
          }
        }
      } catch {
        // не JSON body — игнорируем, обычный 403/410/400 другого происхождения.
      }
    }

    // Проверяем заголовок X-Session-Warning
      const warningHeader = response.headers.get('x-session-warning');
      if (warningHeader) {
        const secondsLeft = parseInt(warningHeader, 10);
        if (!isNaN(secondsLeft)) {
          sessionService.showWarning(secondsLeft);
        }
      }

    // Если получили 401, проверяем причину и пробуем обновить токен
    if (response.status === 401) {
      const sessionExpiredHeader = response.headers.get('x-session-expired');

      // Если сессия истекла из-за неактивности - используем sessionService
      if (sessionExpiredHeader === 'inactivity') {
        sessionService.handleSessionExpired();
        return response;
      }

      // Проверяем причину 401
      const reason = await this.check401Reason(response);

      // Если сессия инвалидирована администратором или пользователь заблокирован - сразу выходим
      if (reason === 'session_invalidated') {
        showToast('Ваша сессия была завершена администратором', 'warning', 2000);
        setTimeout(() => {
          void this.redirectToLoginWithReason('session_invalidated');
        }, 2000);
        return response;
      }

      if (reason === 'blocked') {
        showToast('Ваш аккаунт был заблокирован', 'error', 2000);
        setTimeout(() => {
          void this.redirectToLoginWithReason('blocked');
        }, 2000);
        return response;
      }

      // Иначе пробуем обновить токен
      try {
        await this.refreshToken();
        // Повторяем запрос с новым токеном
        const newHeaders = this.getAuthHeaders();
        response = await fetch(url, {
          ...options,
          headers: {
            ...newHeaders,
            ...options.headers,
          },
        });
      } catch (error) {
        // Если не удалось обновить токен, выходим
        showToast('Сессия истекла, пожалуйста, войдите снова', 'error', 1500);
        setTimeout(() => {
          void this.redirectToLoginWithReason('token_expired');
        }, 1500);
        return response; // дальнейшая обработка не требуется — будет редирект
      }
    }
    
    // 401 — истёкшая/некорректная сессия (выходим); 403 — недостаточно прав (без разлогина)
    if (response.status === 413) {
      // Слишком большой запрос/тело (например, загрузка файла превышает лимит сервера/Nginx)
      showToast('Слишком большой запрос: проверьте размер загружаемого файла', 'error', 2500);
    } else if (response.status === 401) {
      // Проверяем, не было ли это уже обработано выше
      const sessionExpiredHeader = response.headers.get('x-session-expired');
      if (sessionExpiredHeader !== 'inactivity') {
        // Проверяем причину повторно (на случай если это второй 401 после попытки обновления)
        const reason = await this.check401Reason(response);

        if (reason === 'session_invalidated') {
          showToast('Ваша сессия была завершена администратором', 'warning', 1500);
          setTimeout(() => {
            void this.redirectToLoginWithReason('session_invalidated');
          }, 1500);
        } else if (reason === 'blocked') {
          showToast('Ваш аккаунт был заблокирован', 'error', 1500);
          setTimeout(() => {
            void this.redirectToLoginWithReason('blocked');
          }, 1500);
        } else {
          showToast('Сессия истекла, пожалуйста, войдите снова', 'error', 1500);
          setTimeout(() => {
            void this.redirectToLoginWithReason('token_expired');
          }, 1500);
        }
      }
    } else if (response.status === 403) {
      showToast('Недостаточно прав для выполнения действия', 'error', 2000);
    }

    return response;
  }

  /**
   * Выполнить авторизованный запрос с FormData телом (без установки Content-Type)
   */
  async fetchWithAuthForm(url: string, options: RequestInit = {}): Promise<Response> {
    await this.ensureValidToken();

    // Заголовки авторизации без Content-Type — браузер поставит boundary сам
    const authHeaders = this.getAuthHeaders();
    delete (authHeaders as Record<string, unknown>)['Content-Type'];

    // AB3 Academy sandbox header (см. fetchWithAuth выше) — то же поведение для FormData.
    const sandboxHeaderForm = getSandboxHeaderForRequest(url);
    if (sandboxHeaderForm) {
      (authHeaders as Record<string, string>)[SANDBOX_HEADER_NAME] = sandboxHeaderForm;
    }

    let response = await fetch(url, {
      ...options,
      headers: {
        ...authHeaders,
        ...options.headers,
      },
    });

    // AB3 Academy sandbox interceptors (зеркало fetchWithAuth, FINAL handoff §6)
    if (response.status === 410 || response.status === 403 || response.status === 400) {
      try {
        const peek = await response.clone().json();
        const detail = (peek && (peek as { detail?: unknown }).detail) ?? null;
        const kind = classifySandboxRejection(response.status, detail);
        if (kind) {
          clearSandboxSession(kind);
          if (isPotentialSandboxAttack(kind)) {
            console.warn('[sandbox] header rejected', { status: response.status, detail });
          }
        }
      } catch {
        // не JSON body — игнорируем
      }
    }

    // Проверяем заголовок X-Session-Warning
    const warningHeader = response.headers.get('x-session-warning');
    if (warningHeader) {
      const secondsLeft = parseInt(warningHeader, 10);
      if (!isNaN(secondsLeft)) {
        sessionService.showWarning(secondsLeft);
      }
    }

    if (response.status === 401) {
      const sessionExpiredHeader = response.headers.get('x-session-expired');

      // Если сессия истекла из-за неактивности - используем sessionService
      if (sessionExpiredHeader === 'inactivity') {
        sessionService.handleSessionExpired();
        return response;
      }

      // Проверяем причину 401
      const reason = await this.check401Reason(response);

      // Если сессия инвалидирована администратором или пользователь заблокирован - сразу выходим
      if (reason === 'session_invalidated') {
        showToast('Ваша сессия была завершена администратором', 'warning', 2000);
        setTimeout(() => {
          void this.redirectToLoginWithReason('session_invalidated');
        }, 2000);
        return response;
      }

      if (reason === 'blocked') {
        showToast('Ваш аккаунт был заблокирован', 'error', 2000);
        setTimeout(() => {
          void this.redirectToLoginWithReason('blocked');
        }, 2000);
        return response;
      }

      // Иначе пробуем обновить токен
      try {
        await this.refreshToken();
        const freshHeaders = this.getAuthHeaders();
        delete (freshHeaders as Record<string, unknown>)['Content-Type'];
        response = await fetch(url, {
          ...options,
          headers: {
            ...freshHeaders,
            ...options.headers,
          },
        });
      } catch (error) {
        showToast('Сессия истекла, пожалуйста, войдите снова', 'error', 1500);
        setTimeout(() => {
          void this.redirectToLoginWithReason('token_expired');
        }, 1500);
        return response;
      }
    }

    if (response.status === 413) {
      showToast('Слишком большой запрос: проверьте размер загружаемого файла', 'error', 2500);
    } else if (response.status === 401) {
      // Проверяем, не было ли это уже обработано выше
      const sessionExpiredHeader = response.headers.get('x-session-expired');
      if (sessionExpiredHeader !== 'inactivity') {
        // Проверяем причину повторно (на случай если это второй 401 после попытки обновления)
        const reason = await this.check401Reason(response);

        if (reason === 'session_invalidated') {
          showToast('Ваша сессия была завершена администратором', 'warning', 1500);
          setTimeout(() => {
            void this.redirectToLoginWithReason('session_invalidated');
          }, 1500);
        } else if (reason === 'blocked') {
          showToast('Ваш аккаунт был заблокирован', 'error', 1500);
          setTimeout(() => {
            void this.redirectToLoginWithReason('blocked');
          }, 1500);
        } else {
          showToast('Сессия истекла, пожалуйста, войдите снова', 'error', 1500);
          setTimeout(() => {
            void this.redirectToLoginWithReason('token_expired');
          }, 1500);
        }
      }
    } else if (response.status === 403) {
      showToast('Недостаточно прав для выполнения действия', 'error', 2000);
    }

    return response;
  }
  
  isAuthenticated(): boolean {
    return this.getTokens() !== null;
  }

  /**
   * Обновить access token используя refresh token
   */
  async refreshToken(): Promise<AuthTokens> {
    // Если уже идет процесс обновления, ждем его завершения
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = tabSyncManager.coordinatedRefresh(() => this.performTokenRefresh());

    try {
      const tokens = await this.refreshPromise;
      return tokens;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async performTokenRefresh(): Promise<AuthTokens> {
    // Новый протокол: POST /auth/refresh без тела, refresh_token берется из cookie
    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          // не отправляем Content-Type — тело отсутствует
        },
        // Явно указываем, чтобы куки отправлялись и при возможном cross-origin
        credentials: 'include',
      });

      if (!response.ok) {
        // Проверяем, не отозван ли refresh-токен
        let detail: string | undefined;
        try {
          const errorData = await response.json();
          detail = errorData?.detail;

          console.log('[AuthService] Token refresh failed with detail:', detail);

          // Если refresh-токен отозван - выполняем полную очистку и редирект
          if (typeof detail === 'string' && (detail.includes('отозван') || detail.toLowerCase().includes('revoked'))) {
            console.warn('[AuthService] Refresh token has been revoked, logging out...');

            // Закрываем все WebSocket соединения
            try {
              webSocketManager.closeAll();
            } catch (wsError) {
              console.error('[AuthService] Error closing WebSocket connections:', wsError);
            }

            // Очищаем токены
            this.removeTokens();
            this.currentToken = null;
            this.clearTokenRefreshTimer();

            // Показываем уведомление
            showToast('Ваша сессия была завершена. Пожалуйста, войдите снова.', 'warning', 2000);

            // Немедленный редирект на страницу логина
            setTimeout(() => {
              window.location.href = '/login?reason=token_revoked';
            }, 100);

            throw new TokenRefreshRedirectError('Refresh token revoked');
          }
        } catch (jsonError) {
          // Если это уже TokenRefreshRedirectError, пробрасываем дальше
          if (jsonError instanceof TokenRefreshRedirectError) {
            throw jsonError;
          }
          // Если не удалось распарсить JSON, продолжаем с обычной ошибкой
          console.error('[AuthService] Failed to parse refresh error response:', jsonError);
        }

        // Для всех остальных ошибок refresh - выполняем очистку и редирект
        console.warn('[AuthService] Token refresh failed, logging out...');

        // Закрываем все WebSocket соединения
        try {
          webSocketManager.closeAll();
        } catch (wsError) {
          console.error('[AuthService] Error closing WebSocket connections:', wsError);
        }

        // Очищаем токены
        this.removeTokens();
        this.currentToken = null;
        this.clearTokenRefreshTimer();

        // Показываем уведомление
        const errorMessage = detail || 'Не удалось обновить сессию';
        showToast(`${errorMessage}. Пожалуйста, войдите снова.`, 'error', 2000);

        // Редирект на страницу логина
        setTimeout(() => {
          window.location.href = '/login?reason=token_refresh_failed';
        }, 100);

        throw new TokenRefreshRedirectError('Failed to refresh token');
      }

      const data = await response.json().catch(() => ({} as Partial<AuthTokens>));
      const access_token = (data as any).access_token as string | undefined;
      const expires_in = Number((data as any).expires_in ?? 0);

      // Обновляем currentToken и localStorage (сохраним старый refresh_token, если хранится)
      const prev = this.getTokens();
      const merged: AuthTokens = {
        access_token: access_token || prev?.access_token || '',
        refresh_token: prev?.refresh_token || '',
        token_type: (data as any).token_type || prev?.token_type || 'bearer',
        expires_in: expires_in > 0 ? expires_in : (prev?.expires_in || 0),
      };

      if (!merged.access_token) {
        throw new Error('No access token in refresh response');
      }

      this.saveTokens(merged);
      this.currentToken = merged.access_token;
      if (merged.expires_in && merged.expires_in > 0) this.scheduleTokenRefresh(merged.expires_in);
      else this.scheduleTokenRefresh(15 * 60); // дефолт 15 минут, если сервер не прислал TTL

      emitAuthTokenRefreshed();

      return merged;
    } catch (error) {
      console.error('[AuthService] Token refresh failed:', error);

      // Если это TokenRefreshRedirectError, редирект уже выполнен - просто пробрасываем ошибку
      if (error instanceof TokenRefreshRedirectError) {
        throw error;
      }

      // Для всех остальных ошибок (network errors и т.д.) выполняем очистку и редирект
      if (error instanceof Error) {
        // Закрываем все WebSocket соединения
        try {
          webSocketManager.closeAll();
        } catch (wsError) {
          console.error('[AuthService] Error closing WebSocket connections:', wsError);
        }

        // Очищаем токены
        this.removeTokens();
        this.currentToken = null;
        this.clearTokenRefreshTimer();

        // Показываем уведомление
        const isNetworkError = error.message.includes('Failed to fetch') || error.message.includes('NetworkError');
        const message = isNetworkError
          ? 'Ошибка сети. Проверьте подключение и войдите снова.'
          : 'Не удалось обновить сессию. Пожалуйста, войдите снова.';

        showToast(message, 'error', 2000);

        // Редирект на страницу логина
        setTimeout(() => {
          window.location.href = '/login?reason=token_refresh_error';
        }, 100);
      }

      throw error;
    }
  }

  /**
   * Обработать обновление токена от другой вкладки
   */
  handleCrossTabTokenUpdate(accessToken: string, expiresIn: number): void {
    this.currentToken = accessToken;
    if (expiresIn > 0) this.scheduleTokenRefresh(expiresIn);
  }

  /**
   * Обработать logout из другой вкладки
   */
  handleCrossTabLogout(): void {
    if (window.location.pathname === '/login') return;
    try { webSocketManager.closeAll(); } catch { /* ignore */ }
    this.clearTokenRefreshTimer();
    this.removeTokens();
    this.clearTelephonyData();
    this.currentToken = null;
    this.refreshPromise = null;
    window.location.href = '/login?reason=logout_other_tab';
  }

  /**
   * Проверить и при необходимости обновить токен
   */
  private async ensureValidToken(): Promise<void> {
    const tokens = this.getTokens();
    if (!tokens) return;

    // Проверяем, не истекает ли токен в ближайшие 30 секунд
    const tokenExpiry = this.getTokenExpiry();
    if (tokenExpiry && tokenExpiry - Date.now() < 30000) {
      try {
        // В cookie-flow не требуется refresh_token в localStorage
        await this.refreshToken();
        // Если refresh_token отсутствует — не бросаем ошибку, дадим запросу пройти,
        // 401 обработается в fetchWithAuth (logout + редирект на /login)
      } catch (e) {
        console.error('Preflight token refresh failed:', e);
      }
    }
  }

  /**
   * Запланировать автоматическое обновление токена
   */
  private scheduleTokenRefresh(expiresIn: number): void {
    this.clearTokenRefreshTimer();
    
    // Обновляем токен за 1 минуту до истечения
    const refreshTime = (expiresIn - 60) * 1000;
    
    if (refreshTime > 0) {
      this.tokenExpiryTimer = setTimeout(async () => {
        if (!tabSyncManager.isRefreshLeader()) return; // Только лидер выполняет scheduled refresh
        try {
          await this.refreshToken();
        } catch (error) {
          console.error('Auto token refresh failed:', error);
        }
      }, refreshTime);
    }
  }

  /**
   * Очистить таймер обновления токена
   */
  private clearTokenRefreshTimer(): void {
    if (this.tokenExpiryTimer) {
      clearTimeout(this.tokenExpiryTimer);
      this.tokenExpiryTimer = null;
    }
  }

  /**
   * Получить время истечения токена
   */
  private getTokenExpiry(): number | null {
    const tokens = this.getTokens();
    if (!tokens) return null;
    const payload = this.decodeJwtPayload(tokens.access_token);
    return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
  }
}

const authService = new AuthService();
sessionService.setLogoutHandler(() => authService.logout());

// Инициализация координации refresh-токена и logout между вкладками
tabSyncManager.start();
tabSyncManager.onTokenUpdate((accessToken, expiresIn) => {
  authService.handleCrossTabTokenUpdate(accessToken, expiresIn);
});
tabSyncManager.onLogout(() => {
  authService.handleCrossTabLogout();
});

export default authService;
